// src/components/instructor/OrgRosterReview.js — Pass 47B §10/§11: the 26/27 Roster
// Review workflow. Groups longitudinal people by current-season team state and lets
// authorised ops confirm 26/27 participation + category. Writes the canonical
// seasonMemberships model (via onSaveRoster) — NOT a new roster collection, and never
// notifies an instructor merely for being marked as returning.
import React, { useMemo, useState } from 'react';
import { resolveCurrentTeamState, resolveInstructorCategory, TEAM_STATE, CURRENT_SEASON_ID, isOperationalStaff } from '../../utils/instructorCategory';

const V = { t1: '#1C1B18', t2: '#57564F', t3: '#8A8980', border: 'rgba(0,0,0,0.08)', card: '#FFFFFF', blue: '#2563EB', purple: '#9333EA', amber: '#D97706', green: '#16A34A', surface: '#F5F4F0' };
const F = "'DM Sans',system-ui,sans-serif";
const GROUP_LABEL = { [TEAM_STATE.CURRENT]: 'Confirmed current (2026/27)', [TEAM_STATE.REVIEW]: 'Needs roster review', [TEAM_STATE.PREVIOUS]: 'Previous staff' };
const GROUP_ORDER = [TEAM_STATE.CURRENT, TEAM_STATE.REVIEW, TEAM_STATE.PREVIOUS];

export default function OrgRosterReview({ members = [], onSaveRoster }) {
  const [saving, setSaving] = useState('');
  const instructorMembers = useMemo(() => members.filter(isOperationalStaff), [members]);

  const grouped = useMemo(() => {
    const g = { [TEAM_STATE.CURRENT]: [], [TEAM_STATE.REVIEW]: [], [TEAM_STATE.PREVIOUS]: [] };
    for (const m of instructorMembers) g[resolveCurrentTeamState(m)].push(m);
    return g;
  }, [instructorMembers]);

  const save = async (m, patch) => {
    if (!onSaveRoster) return;
    setSaving(m.id);
    try { await onSaveRoster(m, patch); } finally { setSaving(''); }
  };

  const Row = (m) => {
    const cur26 = m.seasonMemberships?.[CURRENT_SEASON_ID]?.category || '';
    // Pass 49A §9: participation is the primary current signal (NOT category). Legacy
    // fallback: explicit currentTeam, else category-implies-current, else unknown.
    const participation = m.seasonMemberships?.[CURRENT_SEASON_ID]?.participation
      || (m.currentTeam === false ? 'not_returning' : (m.currentTeam === true || cur26 ? 'current' : 'unknown'));
    const prev25 = resolveInstructorCategory(m, { season: '2025_26' });
    const evidence = m.seasonMemberships?.[CURRENT_SEASON_ID]?.source;
    return (
      <div key={m.id} data-testid={`roster-row-${m.userId}`} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1.1fr 1.1fr', gap: 8, alignItems: 'center', padding: '8px 10px', borderTop: `1px solid ${V.border}`, fontSize: 12.5 }}>
        <div>
          <div style={{ fontWeight: 700, color: V.t1 }}>{m.name || m.userId}</div>
          {evidence && <div style={{ fontSize: 10, color: V.green }} title={evidence}>● source-confirmed</div>}
          {m.currentTeam === false && <div style={{ fontSize: 10, color: V.t3 }}>historical-only</div>}
        </div>
        <div style={{ color: V.t3 }}>25/26: {prev25 === 'unclassified' ? '—' : prev25}</div>
        {/* 26/27 participation */}
        <select data-testid={`participation-${m.userId}`} value={participation} onChange={(e) => { const v = e.target.value; save(m, { participation: v }); }} disabled={saving === m.id} style={sel}>
          <option value="unknown">Unknown</option>
          <option value="current">Current</option>
          <option value="not_returning">Not returning</option>
        </select>
        {/* 26/27 category */}
        <select data-testid={`category-${m.userId}`} value={cur26 || ''} onChange={(e) => save(m, { category: e.target.value })} disabled={saving === m.id} style={sel}>
          <option value="">Unclassified</option>
          <option value="school">SAS School</option>
          <option value="collective">Collective</option>
        </select>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: F }}>
      <div style={{ fontSize: 12.5, color: V.t2, marginBottom: 10 }}>
        Confirm who is on the <strong>2026/27</strong> team. 25/26 is shown as reference only — it is <em>not</em> carried forward automatically.
        Saving writes canonical season membership; it does not notify anyone.
      </div>
      {GROUP_ORDER.map((state) => (
        <div key={state} style={{ marginBottom: 16, background: V.card, border: `1px solid ${V.border}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '8px 10px', background: V.surface, fontWeight: 700, fontSize: 12.5, color: V.t1, display: 'flex', justifyContent: 'space-between' }}>
            <span>{GROUP_LABEL[state]}</span><span style={{ color: V.t3 }}>{grouped[state].length}</span>
          </div>
          {grouped[state].length === 0
            ? <div style={{ padding: 12, color: V.t3, fontSize: 12 }}>None.</div>
            : grouped[state].map(Row)}
        </div>
      ))}
    </div>
  );
}
const sel = { fontFamily: F, fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.15)', background: '#fff' };
